import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { differenceInYears } from 'date-fns';
import { Form } from 'react-bootstrap';
import { Button, createTheme, ThemeProvider, FormGroup, FormHelperText, CircularProgress, Tooltip } from '@mui/material';
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import {
  getBasicInfo,
  setBasicInfo,
  setSessionStorageItem,
  getSessionStorageItem,
  SESSION_STORAGE_ACCOUNT_KEY,
  SESSION_STORAGE_SAVED_QUOTE_DATA,
  SESSION_STORAGE_TERM_SORT_FILTER_KEY
} from "../../../../sessionStorage";
import { customValidators, object, string, date } from "../../../Helpers/Validators/customValidators";
import { SelectInput, HeightInput, WeightInput, KeyboardDatePickerInput } from "../../../Helpers/Inputs";
import { GetMCTQuotes } from "../Common/GetMCTQuotes";
import QuoterFilter, { getSavedFiltersForCurrentFlow } from "../Helpers/QuoterFilter";
import States from "../../../Helpers/Lists/States";
import Age from "../../../Helpers/Dates/Age";
import { QuoteProductID } from "../../Common/QuoteProductID";
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import { getFaceAmountFromAge } from "../../../Helpers/Lists/CoverageAmounts/AmericoCoverageAmounts";
import { getTermLengthFromAge } from "../../../Helpers/Lists/CoverageLengths/AmericoCoverageLengths";
import { calculateExactAge, calculateNearestAge } from "../../Common/Helpers/AgeCalculation";
import TestimonialPanel from "../../../Common/Testimonials/TestimonialPanel";
import { isHeightInRange, isWeightInRange } from "../../../Common/CheckValidHeightOrWeight";
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { COVR_DISCLAIMER, GENDER_OPTIONS, MIN_MAX_RANGES, TOBACCO_OPTIONS } from "../Helpers/Constant";
import "./AboutMeMinimalForm.scss";

const AboutMeMinimalForm = forwardRef((props, ref) => {
  const innerRef = useRef(null);
  // Persist the quote debounce timer across renders so rapid edits cancel the previous quote request.
  const quoteDebounceRef = useRef(null);
  const { isPartnerReferralEmailCaptureActive, enablePartnerReferralEmailCapture } = props.emailCapture || {};

  // Session-backed values are only needed as initial inputs for this form.
  const termSettings = useMemo(() => getSessionStorageItem(SESSION_STORAGE_TERM_SORT_FILTER_KEY), []);

  // Creating a MUI theme is expensive; only rebuild when the brand color changes.
  const theme = useMemo(() => createTheme({
    palette: {
      primary: {
        main: `${props.brandTheme ? props.brandTheme : "#3f51b5"}`,
      },
    },
    typography: {
      fontFamily: "museo-sans",
      body1: {
        fontSize: "0.813rem",
        fontWeight: 500,
      },
    },
    components: {
      MuiTypography: {
        styleOverrides: {
          h4: {
            fontSize: "1.688rem",
            paddingLeft: 8,
            paddingRight: 8,
          },
          subtitle1: {
            fontSize: "1.188rem",
            paddingLeft: 8,
            paddingRight: 8,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          gutters: {
            paddingLeft: `${12}px !important`,
            paddingRight: `${12}px !important`,
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            height: 42,
            maxWidth: 195,
            textTransform: "capitalize !important",
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: 10,
            [theme.breakpoints.down('sm')]: {
              paddingLeft: 0,
              paddingRight: 5,
            },
          }),
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            height: 42,
            backgroundColor: "#fff",
          },
          input: {
            fontSize: "1rem",
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
          },
          underline: {
            "&::before": {
              borderBottom: "solid 2px #b2b2b2",
            },
            "&:hover::before": {
              borderBottomWidth: "2px",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          input: ({ theme }) => ({
            padding: "13px 0px 13px 13px",
            [theme.breakpoints.down('sm')]: {
              paddingLeft: 8,
            },
          }),
          inputAdornedEnd: {
            paddingRight: "0px !important",
          },
          adornedEnd: {
            paddingRight: "0px !important",
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem !important",
            fontWeight: "500 !important",
            color: "rgba(0,0,0,0.6)",
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginTop: 0,
            paddingTop: 0,
            '&.Mui-error': {
              color: '#f44336',
              fontSize: '13px',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          outlined: {
            transform: "translate(14px, 11px) scale(1)",
          },
          shrink: {
            color: "rgba(0,0,0,1) !important",
            fontWeight: 600,
            transform: "translate(12px, -8px) scale(0.75) !important",
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            marginBottom: '10px !important',
          },
          thumb: {
            height: '12px !important',
            width: '12px !important',
          },
        },
      },
    },
  }), [props.brandTheme]);

  const navigate = useNavigate();
  // Basic info seeds the form and quote metadata; memoize to avoid parsing session storage on each keystroke.
  const basicInfo = useMemo(() => getBasicInfo(), []);
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState(() => AboutMeMinimalForm.DefaultFormState(false, basicInfo));
  const [purlMismatch, setPurlMismatch] = useState(false);
  const [initialBasicInfo] = useState(() => basicInfo);
  const [tobaccoPopover, setTobaccoPopover] = useState(null);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  // Age-derived quote defaults only need to update when DOB changes.
  const age = useMemo(() => Age(formState.birthdate), [formState.birthdate]);
  const coverageAmountAsPerAge = useMemo(
    () => (basicInfo && basicInfo.faceAmount) || getFaceAmountFromAge(age),
    [age, basicInfo]
  );
  const termLengthAsPerAge = useMemo(
    () => (basicInfo && basicInfo.termLength) || getTermLengthFromAge(age),
    [age, basicInfo]
  );

  const [filterObject, setFilterObject] = useState(() => getSavedFiltersForCurrentFlow(termSettings, props.isSimplifiedIssueTerm === true));
  const [termLength, setTermLength] = useState(20);
  const [coverageAmount, setCoverageAmount] = useState(500000);
  const [healthClass, setHealthClass] = useState('PP');
  const [filteredQuoteResults, setFilteredQuoteResults] = useState([]);
  const [fastestRate, setFastestRate] = useState('');
  const [lowestPrice, setLowestPrice] = useState('');

  // Avoid repeated JSON parsing and product lookup during controlled field renders.
  const accountObj = useMemo(() => getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY), []);
  const savedQuote = useMemo(() => getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA) || {}, []);
  const termNavigationState = useMemo(
    () => props.isSimplifiedIssueTerm ? { state: { isFromSimplifiedIssueTerm: true } } : undefined,
    [props.isSimplifiedIssueTerm]
  );
  const accountProductTypeSettings = useMemo(() => (
    accountObj?.accountProducts?.find(product => props.isSimplifiedIssueTerm
      ? product.productLandingUrl === '/landing/simplified-issue-term'
      : product.productLandingUrl === '/landing/term-insurance' || product.productLandingUrl === '/landing/loan-protection')
  ), [accountObj, props.isSimplifiedIssueTerm]);

  const [quoteResults, setQuoteResults] = useState([]);
  const [quoteLowestPrice, setQuoteLowestPrice] = useState(null);
  const [quoteFastestProcess, setQuoteFastestProcess] = useState(null);
  const [quoteMostPopular, setQuoteMostPopular] = useState(null);
  const [saveQuoteData, setSaveQuoteData] = useState(null);
  const [saveQuoteContact, setSaveQuoteContact] = useState(savedQuote);
  const [isQuoteSaved, setQuoteSaved] = useState(savedQuote?.quoteSaved);

  const handleTobaccoPopoverOpen = useCallback((event) => {
    setTobaccoPopover(event.currentTarget);
  }, []);

  const handleTobaccoPopoverClose = useCallback(() => {
    setTobaccoPopover(null);
  }, []);

  const open = Boolean(tobaccoPopover);

  // Keep the Yup resolver stable so RHF does not receive a new schema on every render.
  const validationSchema = useMemo(
    () => AboutMeMinimalForm.ValidationSchema(props.ageSpecs, MIN_MAX_RANGES),
    [props.ageSpecs]
  );

  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    // Validate on blur/submit so masked inputs stay responsive while users are typing.
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: formState,
  });

  const { handleSubmit, formState: { errors }, trigger } = formMethods;
  useEffect(() => {
    if (saveQuoteContact?.emailAddress) {
      setSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA, {
        "quoteSaved": isQuoteSaved,
        "firstName": saveQuoteContact?.firstName,
        "lastName": saveQuoteContact?.lastName,
        "emailAddress": saveQuoteContact?.emailAddress,
      });
    }
    setCurrentSavedQuoteData();
  }, [isQuoteSaved]);

  const sorter = useCallback((a, b, sortByField) => {
    switch (sortByField) {
      case 'lowestPrice':
        return a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore;
      case 'highestPrice':
        return b.monthlyRate - a.monthlyRate || a.totalUnweightedScore - b.totalUnweightedScore;
      case 'speed':
        return b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore;
      case 'bestSellers':
        return
      case 'highestRated':
        return b.companyRatingsScore - a.companyRatingsScore || a.totalUnweightedScore - b.totalUnweightedScore;
      case 'fastest':
        return b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore;
      default:
        break;
    }
  }, []);

  const filterResults = useCallback((quotes) => {
    return QuoterFilter.filterResults(filterObject, quotes);
  }, [filterObject]);

  useEffect(() => {
    if (quoteResults.length > 0) {
      quoteResults.sort((a, b) => b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
      quoteResults[0].isFastest = true;
      setFastestRate(quoteResults[0].monthlyRate);
      quoteResults.sort((a, b) => a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
      setLowestPrice(quoteResults[0].monthlyRate);
      quoteResults[0].isBestPrice = true;

      quoteResults.sort((a, b) => sorter(a, b, (!!props.quoteResultSortDefault ? props.quoteResultSortDefault : 'lowestPrice')));
      const filteredQuotes = filterResults(quoteResults);
      setFilteredQuoteResults(filteredQuotes);

    } else {
      setFilteredQuoteResults([]);
    }
  }, [filterResults, props.quoteResultSortDefault, quoteResults, sorter]);

  const getQuoteFastestProcess = useCallback((results, lowest) => {
    // Sort a copy so quote state is not mutated while deriving the fastest process card.
    const sortedResults = [...results].sort((a, b) => a.speedScore - b.speedScore);
    const product = results.find(product => !!product.isFastest);

    if (product && lowest && product.productID === lowest.productID) {
      return sortedResults[sortedResults.length - 2];
    }
    return product;
  }, []);

  const getQuoteMostPopular = useCallback((results, fastest, lowest) => {
    const productIds = [QuoteProductID.LGA_PRODUCT_ID, QuoteProductID.BANNER_OPTERM_PRODUCT_ID, QuoteProductID.PENN_OPTERM_PRODUCT_ID, QuoteProductID.SYMETRA_SWIFT_TERM_PRODUCT_ID, QuoteProductID.PACIFIC_LIFE_PROMISE_TERM_PRODUCT_ID, QuoteProductID.AMERICO_TERM_PRODUCT_ID];

    if (!props.isAmerico && fastest && lowest) {
      let product = null;

      for (const id of productIds) {
        product = results.find(product => product.productID === id && fastest.productID !== id && lowest.productID !== id);
        if (product) {
          break;
        }
      }
      return product;
    }
    if (props.isAmerico && fastest && lowest) {
      let product = null;

      product = results.find(product => product.productID === QuoteProductID.AMERICO_TERM_PRODUCT_ID && fastest.productID !== QuoteProductID.AMERICO_TERM_PRODUCT_ID && lowest.productID !== QuoteProductID.AMERICO_TERM_PRODUCT_ID);

      if (product) {
        return product;
      } else {

        for (const id of productIds) {
          product = results.find(product => product.productID === id && fastest.productID !== id && lowest.productID !== id);
          if (product) {
            break;
          }
        }
        return product;
      }
    }
  }, [props.isAmerico]);

  const setQuoteValues = useCallback(() => {
    const quotedLowestPrice = quoteResults.find(product => !!product.isBestPrice);
    const quotedFastestProcess = getQuoteFastestProcess(quoteResults, quotedLowestPrice);
    const quotedMostPopular = getQuoteMostPopular(quoteResults, quotedFastestProcess, quotedLowestPrice);

    const lowestPriceOdj = {
      carrierLogo: quotedLowestPrice && quotedLowestPrice.logoUrl,
      rating: quotedLowestPrice && quotedLowestPrice.rating,
      monthlyRate: quotedLowestPrice && quotedLowestPrice.monthlyRate && quotedLowestPrice.monthlyRate.toFixed(2),
    };

    const fastestProcessOdj = {
      carrierLogo: quotedFastestProcess && quotedFastestProcess.logoUrl,
      rating: quotedFastestProcess && quotedFastestProcess.rating,
      monthlyRate: quotedFastestProcess && quotedFastestProcess.monthlyRate && quotedFastestProcess.monthlyRate.toFixed(2),
    };

    const mostPopularOdj = {
      carrierLogo: quotedMostPopular && quotedMostPopular.logoUrl,
      rating: quotedMostPopular && quotedMostPopular.rating,
      monthlyRate: quotedMostPopular && quotedMostPopular.monthlyRate && quotedMostPopular.monthlyRate.toFixed(2),
    };

    setQuoteLowestPrice(lowestPriceOdj);
    setQuoteFastestProcess(fastestProcessOdj);
    setQuoteMostPopular(mostPopularOdj);
  }, [getQuoteFastestProcess, getQuoteMostPopular, quoteResults]);

  const setCurrentSavedQuoteData = useCallback(() => {
    const newData = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
    setSaveQuoteContact(newData);
  }, []);

  const trimString = useCallback((str) => {
    if (str !== null) {
      return str?.replace(/\s+/g, " ");
    }
    return "";
  }, []);

  useEffect(() => {
    if (props.saveQuoteContact) {
      setSaveQuoteContact(props.saveQuoteContact);
    }
  }, [props.saveQuoteContact]);

  useEffect(() => {
    if (quoteResults.length > 0) {
      setQuoteValues();
    }
  }, [loading, quoteResults.length, setQuoteValues]);

  useEffect(() => {
    if (!accountObj?.account) {
      setSaveQuoteData(null);
      return;
    }

    const quoteData = {
      accountId: accountObj.account.accountId,
      accountName: accountObj.account.accountName,
      oneClickAccountId: accountObj.account.oneClickAccountId,
      accountProductTypeSettingId: accountProductTypeSettings?.accountProductTypeSettingId,
      productType: props.isAmerico ? "LoanProtector" : "TermA",
      sessionId: accountObj.sessionId,
      accountColorLogoUrl: accountObj.account.logoUrl,
      firstName: saveQuoteContact?.firstName,
      lastName: saveQuoteContact?.lastName,
      emailAddress: saveQuoteContact?.emailAddress,
      gender: basicInfo.gender,
      dateOfBirth: basicInfo.birthdate,
      state: basicInfo.state,
      height: trimString(basicInfo.height),
      weight: trimString(basicInfo.weight),
      tobaccoStatus: props.isAmerico ? (basicInfo.tobacco !== 'Never' && basicInfo.tobacco !== 'false') : basicInfo.tobacco,
      coverageAmount: coverageAmount,
      termLength: termLength,
      healthClass: healthClass,
      quoteLowestPrice: quoteLowestPrice,
      quoteFastestProcess: quoteFastestProcess,
      quoteMostPopular: quoteMostPopular,
      quotedLowestPrice: !!quoteLowestPrice ? quoteLowestPrice.monthlyRate : null,
      signatureEmail: "insuranceapplication@covrtech.com",
      signaturePhone: accountObj.account.phone,
      covrDisclaimer: COVR_DISCLAIMER,
      disclaimerHtml: accountObj.account.disclaimerHtml ? accountObj.account.disclaimerHtml : null,
    };
    setSaveQuoteData(quoteData);
  }, [accountObj, accountProductTypeSettings, basicInfo, coverageAmount, healthClass, props.isAmerico, quoteFastestProcess, quoteLowestPrice, quoteMostPopular, saveQuoteContact, termLength, trimString]);

  useEffect(() => {
    props.setSaveQuoteData?.(saveQuoteData);
  }, [props.setSaveQuoteData, saveQuoteData]);

  useEffect(() => {
    setSubmitDisabled(!!isPartnerReferralEmailCaptureActive && !!enablePartnerReferralEmailCapture);
  }, [enablePartnerReferralEmailCapture, isPartnerReferralEmailCaptureActive]);

  useEffect(() => {
    props.setEmailCaptureActive?.(submitDisabled);
  }, [props.setEmailCaptureActive, submitDisabled]);

  useEffect(() => {
    if (!props.isPurlVerification || !props.selectedQuote || initialBasicInfo.birthdate) {
      return undefined;
    }

    let ignore = false;

    (async () => {
      let mismatch = false;
      if (formState.birthdate !== '' && (await trigger('birthdate'))) {
        let initialAge, newAge;
        if (props.selectedQuote.ageDetermination && props.selectedQuote.ageDetermination.toLowerCase() === 'nearest') {
          initialAge = calculateNearestAge(new Date(initialBasicInfo.fakeBirthdate));
          newAge = calculateNearestAge(new Date(formState.birthdate));
        } else {
          initialAge = calculateExactAge(new Date(initialBasicInfo.fakeBirthdate));
          newAge = calculateExactAge(new Date(formState.birthdate));
        }
        mismatch = mismatch || initialAge !== newAge;
      }
      mismatch = mismatch || initialBasicInfo.gender !== formState.gender;
      mismatch = mismatch || initialBasicInfo.state !== formState.state;
      mismatch = mismatch || initialBasicInfo.tobacco !== formState.tobacco;
      if (!ignore) {
        setPurlMismatch(mismatch);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [
    formState.birthdate,
    formState.gender,
    formState.state,
    formState.tobacco,
    initialBasicInfo,
    props.isPurlVerification,
    props.selectedQuote,
    trigger,
  ]);

  useEffect(() => {
    // Contact-specialist eligibility is independent from quote/PURL validation, so keep it in a narrow effect.
    if (formState.weight || formState.height || formState.birthdate) {
      const age = differenceInYears(new Date(), new Date(formState.birthdate))
      props.setIsContactSpecialist?.(
        isWeightInRange(formState.weight, MIN_MAX_RANGES.minWeight, MIN_MAX_RANGES.maxWeight) ||
        isHeightInRange(formState.height, MIN_MAX_RANGES.minHeightInches, MIN_MAX_RANGES.maxHeightInches) ||
        age > props.ageSpecs.max ||
        age < props.ageSpecs.min
      );
    }
  }, [
    formState.birthdate,
    formState.height,
    formState.weight,
    props.ageSpecs.max,
    props.ageSpecs.min,
    props.setIsContactSpecialist,
  ]);

  const isValidHeight = useCallback((height) => {
    if (height) {
      const [feetPart, inchesPart] = height.split('ft');
      const feet = feetPart?.trim();
      const inches = inchesPart?.split('in')[0]?.trim();
      if (feet > 0 && inches >= 0 && inches !== "") {
        return true;
      }
      return false;

    }
    return false;
  }, []);

  const isValidWeight = useCallback((weight) => {
    const wts = parseInt(weight);
    if (weight && wts >= 78 && wts <= 395) {
      const wt = weight.split(' ')[0].trim();
      if (wt) {
        return true;
      }
      return false;

    }
    return false;
  }, []);

  const isValidDate = useCallback((birthdate) => {
    const date = new Date(birthdate);
    const age = Age(birthdate);
    return (date !== "Invalid Date" && age <= props.ageSpecs.max && age >= props.ageSpecs.min);
  }, [props.ageSpecs.max, props.ageSpecs.min]);

  const isValidBasicInfo = useCallback((basicFormState) => {
    if (basicFormState.gender && basicFormState.state && basicFormState.tobacco
      && isValidHeight(basicFormState.height) && isValidWeight(basicFormState.weight) && isValidDate(basicFormState.birthdate)) {
      return true;
    }
    return false;
  }, [isValidDate, isValidHeight, isValidWeight]);

  const runQuote = useCallback((quoteFormState, getIsSubscribed) => {
    const coverageAmount = coverageAmountAsPerAge;
    const termLength = termLengthAsPerAge;
    let healthClass = 'PP';

    if (props.teaserQuoteParams && props.teaserQuoteParams.healthClass) {
      healthClass = props.teaserQuoteParams.healthClass;
    }

    GetMCTQuotes(quoteFormState, healthClass, coverageAmount.toString(10), termLength.toString(10), setLoading, getIsSubscribed, null, setQuoteResults, ['Level'], null, null, false, true);
  }, [coverageAmountAsPerAge, props.teaserQuoteParams, termLengthAsPerAge]);

  const getQuotes = useCallback(() => {
    if (!isValidBasicInfo(formState)) {
      return undefined;
    }

    setLoading(true);
    let isSubscribed = true;

    // Cancel the previous pending quote run before scheduling a new one for the latest valid form state.
    if (quoteDebounceRef.current) {
      clearTimeout(quoteDebounceRef.current);
    }

    const quoteFormState = formState;
    quoteDebounceRef.current = setTimeout(() => {
      quoteDebounceRef.current = null;
      runQuote(quoteFormState, () => isSubscribed);
    }, 1000);

    return () => {
      isSubscribed = false;
      if (quoteDebounceRef.current) {
        clearTimeout(quoteDebounceRef.current);
        quoteDebounceRef.current = null;
      }
    };
  }, [formState, isValidBasicInfo, runQuote]);

  const handleValueChange = useCallback((field, value) => {
    // Let RHF's controlled field onChange handle validation; this keeps local state updates instant.
    setFormState((previousFormState) => (
      previousFormState[field] === value
        ? previousFormState
        : { ...previousFormState, [field]: value }
    ));
  }, []);

  useEffect(() => {
    // Clear any delayed quote request if the form unmounts.
    return () => {
      if (quoteDebounceRef.current) {
        clearTimeout(quoteDebounceRef.current);
        quoteDebounceRef.current = null;
      }
    };
  }, []);

  // React Hook Form's errors object can change identity often; expand only when the set of fields changes.
  const errorSignature = useMemo(() => Object.keys(errors).sort().join('|'), [errors]);

  useEffect(() => {
    if (errorSignature) {
      props.handleExpandForm?.(true);
    }
  }, [errorSignature, props.handleExpandForm]);

  useEffect(() => {
    if (!isPartnerReferralEmailCaptureActive) {
      return undefined;
    }
    return getQuotes();
  }, [getQuotes, isPartnerReferralEmailCaptureActive]);

  // Option and date values are stable props for memoized input components.
  const stateOptions = useMemo(
    () => props.unavailableStates ? StatesWithDisabled(props.unavailableStates) : States(),
    [props.unavailableStates]
  );

  const maxBirthDate = useMemo(() => dayjs(), []);
  const minBirthDate = useMemo(
    () => dayjs().subtract((props.ageSpecs.max) + 30, "year").subtract(12, "month").subtract(-1, "day"),
    [props.ageSpecs.max]
  );
  const birthdatePickerValue = useMemo(() => {
    const value = formState.birthdate;
    if (!value) {
      return null;
    }
    return typeof value === 'string' ? dayjs(value) : value;
  }, [formState.birthdate]);
  const birthdateAge = useMemo(
    () => formState.birthdate ? differenceInYears(new Date(), new Date(formState.birthdate)) : null,
    [formState.birthdate]
  );
  // Precompute helper-note visibility so render does not repeat parsing/range checks.
  const showBirthdateSpecialNote = formState.birthdate
    && (birthdateAge > props.ageSpecs.max || birthdateAge < props.ageSpecs.min);
  const showHeightSpecialNote = formState.height
    && isHeightInRange(formState.height, MIN_MAX_RANGES.minHeightInches, MIN_MAX_RANGES.maxHeightInches);
  const weightMatch = formState.weight?.match(/(\d+)/);
  const showWeightSpecialNote = formState.weight
    && isWeightInRange(formState.weight, MIN_MAX_RANGES.minWeight, MIN_MAX_RANGES.maxWeight)
    && weightMatch
    && weightMatch[0] <= MIN_MAX_RANGES.maxSliderWeight;

  // Stable handlers prevent memoized child inputs from re-rendering because of new callback identities.
  const handleGenderChange = useCallback((e) => handleValueChange('gender', e.target.value), [handleValueChange]);
  const handleBirthdateChange = useCallback((date) => {
    const value = date && dayjs(date).isValid() ? dayjs(date).format('MM/DD/YYYY') : date;
    handleValueChange('birthdate', value);
  }, [handleValueChange]);
  const handleStateChange = useCallback((e) => handleValueChange('state', e.target.value), [handleValueChange]);
  const handleHeightChange = useCallback((value) => handleValueChange('height', value), [handleValueChange]);
  const handleWeightChange = useCallback((value) => handleValueChange('weight', value), [handleValueChange]);
  const handleTobaccoChange = useCallback((e) => handleValueChange('tobacco', e.target.value), [handleValueChange]);

  useImperativeHandle(ref, () => ({
    onSubmit,
    dispatchSubmit: () => {
      innerRef.current?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    get element() {
      return innerRef.current;
    }
  }));

  const onSubmit = () => {
    // Save formState to basicinfo
    const basicInfoFromSessionStorage = getBasicInfo();
    basicInfoFromSessionStorage.gender = formState.gender;
    basicInfoFromSessionStorage.birthdate = formState.birthdate;
    basicInfoFromSessionStorage.height = formState.height;
    basicInfoFromSessionStorage.weight = formState.weight;
    basicInfoFromSessionStorage.state = formState.state;
    basicInfoFromSessionStorage.tobacco = formState.tobacco;
    basicInfoFromSessionStorage.usResident = formState.usResident;

    setBasicInfo(basicInfoFromSessionStorage);

    if (submitDisabled && isPartnerReferralEmailCaptureActive) {
      props.setModalSaveQuoteIsOpen(submitDisabled);
    }

    if (props.openEditPopUp) {
      const path = props.isAmerico ? '/loan-protection/quote-results'
        : props.isContactSpecialist ? '/select-appointment' : '/term-insurance/quote-results';
      navigate(path, path === '/term-insurance/quote-results' ? termNavigationState : undefined);
      props.setOpenEditPopUp(false, purlMismatch);
    } else {
      props.isContactSpecialist ? navigate('/select-appointment') : props.onNext();
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="form">
        <FormProvider {...formMethods}>
          <Form onSubmit={handleSubmit(onSubmit)} ref={innerRef} className={props.stackForm ? "stack" : ""}>
            <FormGroup>
              <SelectInput
                variant={"outlined"}
                name="gender"
                id="gender"
                className="genderField"
                label="Gender"
                value={formState.gender}
                onChange={handleGenderChange}
                selectOptions={GENDER_OPTIONS} />
            </FormGroup>
            <div className="form-control-group">
              <FormGroup>
                <KeyboardDatePickerInput
                  label="Date of Birth"
                  name="birthdate"
                  id="birthdate"
                  className="dateOfBirthField"
                  value={birthdatePickerValue}
                  maxDate={maxBirthDate}
                  minDate={minBirthDate}
                  onChange={handleBirthdateChange}
                  variant={"outlined"}
                  disableFuture
                />
                {showBirthdateSpecialNote &&
                  <FormHelperText sx={{ marginTop: '1px !important' }}>
                    <Tooltip
                      title="You're not eligible due to age restrictions, but you can continue and our specialists can help find the right product for you. Contact us today!"
                      placement="bottom-start"
                      slotProps={{
                        tooltip: {
                          className: 'light-tooltip ',
                          sx: { fontSize: '13px', padding: '10px', maxWidth: '100%', marginX: '12px' },
                        },
                      }}
                    >
                      <i className='special-note'> <InfoOutlinedIcon sx={{ marginLeft: '2px', fontSize: '0.9rem', opacity: '0.5' }} /> Special Note </i>
                    </Tooltip>
                  </FormHelperText>
                }
              </FormGroup>
              <FormGroup>
                <SelectInput
                  variant={"outlined"}
                  name="state"
                  id="state"
                  className="stateField"
                  value={formState.state}
                  onChange={handleStateChange}
                  label="State"
                  selectOptions={stateOptions} />
              </FormGroup>
            </div>
            <div className="form-control-group">
              <FormGroup>
                <HeightInput
                  variant="outlined"
                  name="height"
                  id="height"
                  label="Height"
                  className="heightWeightFields"
                  value={formState.height}
                  onChange={handleHeightChange}
                  openEditPopUp={props.openEditPopUp}
                  min={MIN_MAX_RANGES.minSliderHeightInches}
                  max={MIN_MAX_RANGES.maxSliderHeightInches}
                />
                {showHeightSpecialNote &&
                  <FormHelperText>
                    <Tooltip
                      title="You're not eligible due to height restrictions, but our specialists can help find the right product for you. Contact us today!"
                      placement="bottom-start"
                      slotProps={{
                        tooltip: {
                          className: 'light-tooltip ',
                          sx: { fontSize: '13px', padding: '10px', maxWidth: '100%', marginX: '12px' },
                        },
                      }}
                    >
                      <i className='special-note'> <InfoOutlinedIcon sx={{ marginLeft: '2px', fontSize: '0.9rem', opacity: '0.5' }} /> Special Note</i>
                    </Tooltip>
                  </FormHelperText>
                }
              </FormGroup>
              <FormGroup>
                <WeightInput
                  variant="outlined"
                  name="weight"
                  id="weight"
                  label="Weight"
                  className="heightWeightFields"
                  value={formState.weight}
                  onChange={handleWeightChange}
                  openEditPopUp={props.openEditPopUp}
                  min={MIN_MAX_RANGES.minSliderWeight}
                  max={MIN_MAX_RANGES.maxSliderWeight}
                />
                {showWeightSpecialNote &&
                  <FormHelperText>
                    <Tooltip
                      title="You're not eligible due to weight restrictions, but our specialists can help find the right product for you. Contact us today!"
                      placement="bottom-start"
                      slotProps={{
                        tooltip: {
                          className: 'light-tooltip ',
                          sx: { fontSize: '13px', padding: '10px', maxWidth: '100%', marginX: '12px' },
                        },
                      }}
                    >
                      <i className='special-note'> <InfoOutlinedIcon sx={{ marginLeft: '2px', fontSize: '0.9rem', opacity: '0.5' }} /> Special Note</i>
                    </Tooltip>
                  </FormHelperText>
                }
              </FormGroup>
            </div>
            <FormGroup>
              <SelectInput
                variant={"outlined"}
                name="tobacco"
                id="tobacco"
                className="tobaccoField"
                value={formState.tobacco}
                onChange={handleTobaccoChange}
                onMouseEnter={handleTobaccoPopoverOpen}
                onMouseLeave={handleTobaccoPopoverClose}
                aria-owns={open ? 'tobacco-popover' : undefined}
                aria-haspopup="true"
                label="Tobacco Use"
                selectOptions={TOBACCO_OPTIONS}
                aria-invalid="true"
                aria-describedby="tobacco" />
            </FormGroup>
            <Button
              variant="contained"
              color="primary" type="submit"
              fullWidth
              className={`submitButton submit-button ${props.isContactSpecialist ? "shrink" : ""}`}
              disabled={loading}
            >{loading ?
              <CircularProgress color="inherit" size={20} /> :
              props.isPurlVerification ? (purlMismatch ? "Back To Results" : "Continue") :
                (props.isContactSpecialist ? "Contact a Specialist" : "See Your Quotes")}</Button>
          </Form>
        </FormProvider>
        <div className={`testimonial-container ${props.stackForm ? "stack" : ""}`} >
          <div className="testimonial">
            <TestimonialPanel />
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
});

AboutMeMinimalForm.ValidationSchema = (ageSpecs, minMaxRanges) => {
  return object().shape({
    gender: string().required('Required'),
    birthdate: date()
      .typeError('Invalid date')
      .required('Required'),
    state: string().required('Required'),
    height: customValidators.string().required('Required')
      .isValidHeightInchString('Invalid Height', minMaxRanges.minSliderHeightInches, minMaxRanges.maxSliderHeightInches),
    weight: customValidators.string().required('Required')
      .isValidWeightString('Invalid Weight', minMaxRanges.minSliderWeight, minMaxRanges.maxSliderWeight, 'Required'),
    tobacco: string().required('Required'),
  });
};

AboutMeMinimalForm.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender) || '',
    birthdate: prefill ? '11/15/1980' : (basicInfo && basicInfo.birthdate) || '',
    tobacco: prefill ? 'No' : (basicInfo && basicInfo.tobacco) || '',
    state: prefill ? 'ID' : (basicInfo && basicInfo.state) || '',
    height: prefill ? '5 ft 10 in' : (basicInfo && basicInfo.height) || '',
    weight: prefill ? '175 pounds' : (basicInfo && basicInfo.weight) || '',
    usResident: true,
  };
};

export default AboutMeMinimalForm;
